import ExportService from "@app/exports/exportService";
import { ColumnConfig, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { ResourceStatus } from "@app/resources";
import { toast } from "react-toastify";
import { formatDateTime } from "@app/shared/formatDateTime";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import { SearchBox } from "@app/shared/SearchBox";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { ColumnConfigurationPanel, PanelMode } from "@app/report-customization/build-reports/components/ColumnConfigurationPanel";
import { reportTypeToColumnsEndpointMap, ReportTypeNamesMap } from "@app/report-customization/build-reports/helpers/helpers";
import { ReportPresetsApiResponse } from "@app/report-customization/manage-presets/interfaces/reportPresetsApiResponse";
import { ReportPresetsTableData } from "@app/report-customization/manage-presets/interfaces/reportPresetsTableData";

const columnConfig: ColumnConfig = {
  actions: {
    displayText: "Actions",
    noSort: true,
    sortOrder: 70,
    testId: "actions",
  },
  createdBy: {
    displayText: "Created By",
    sortOrder: 30,
    testId: "created-by",
  },
  lastUpdated: {
    displayText: "Last Updated",
    sortOrder: 40,
    testId: "last-updated",
  },
  lastUsed: {
    displayText: "Last Used",
    sortOrder: 50,
    testId: "last-used",
  },
  presetName: {
    displayText: "Preset",
    sortOrder: 10,
    testId: "preset",
  },
  privacy: {
    displayText: "Privacy",
    sortOrder: 60,
    testId: "privacy",
  },
  reportType: {
    displayText: "Report Type",
    sortOrder: 20,
    testId: "report-type",
  },
}

export const ManageReportPresets: FC = () => {
  const [searchValue, setSearchValue] = useState("");
  const [processing, setProcessing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [allPrivatePresetNames, setAllPrivatePresetNames] = useState<string[]>([]);
  const [apiResponse, setApiResponse] = useState<ReportPresetsApiResponse[]>([]);
  const [sortedColumn, setSortedColumn] = useState<keyof ReportPresetsTableData>();
  const [sortDirection, setSortDirection] = useState(SortDirections.None);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const user = useContext(UserContext);
  const exportService = useRef(new ExportService()).current;

  const handleDelete = (presetId: number, presetName: string) => {
    setProcessing(true);
    exportService.deletePreset(presetId, ResourceStatus.Purged)
      .then(() => {
        toast(`You have successfully deleted the preset ${presetName}.`, {
          containerId: "z-index-1030",
          type: "success",
        });
        refreshList();
      })
      .catch(() => {
        toast(`Failed to delete preset. Please try again.`, {
          containerId: "z-index-1030",
          type: "error",
        });
      }).finally(() => {
        setProcessing(false);
      });
  };

  const changePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const updateSortParameters = (records: ReportPresetsTableData[], column: keyof ReportPresetsTableData, direction: SortDirections) => {
    setSortedColumn(column);
    setSortDirection(direction);
    setCurrentPage(1);
    return records;
  }

  const sortData = (records: ReportPresetsTableData[]) => {
    if (sortDirection === SortDirections.None) {
      return records;
    }
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;
    switch (sortedColumn) {
      case "createdBy":
      case "privacy":
      case "reportType":
        return records.sort((a, b) => (a[sortedColumn] > b[sortedColumn] ? 1 : -1) * multiplier);
      case "lastUpdated":
      case "lastUsed":
        const rowsWithDates: ReportPresetsTableData[] = [];
        const rowsWithoutDates: ReportPresetsTableData[] = [];
        records.forEach((r) => {
          if (!isNaN(new Date(r[sortedColumn]).getMilliseconds())) {
            rowsWithDates.push(r);
          } else {
            rowsWithoutDates.push(r);
          }
        });
        rowsWithDates.sort((a, b) => ((new Date(a[sortedColumn]) || "") > (new Date(b[sortedColumn]) || "") ? 1 : -1) * multiplier);
        return [...rowsWithDates, ...rowsWithoutDates];
      case "presetName":
        return records.sort((a, b) => (a.presetName.key > b.presetName.key ? 1 : -1) * multiplier);
    }
  }

  const refreshList = () => {
    exportService.getReportPresets().then((response) => {
      const newAllPrivatePresetNames = response.filter((r) => r.isPrivate).map((r) => r.name);
      setAllPrivatePresetNames(newAllPrivatePresetNames);
      setApiResponse(response);
    }).finally(() => {
      setIsInitialLoad(false);
    })
  }

  const isUserClientAdmin: boolean = user.roles.includes(Roles.CLIENT_ADMIN);
  const isUserReportPresetAdmin: boolean = user.roles.includes(Roles.REPORT_PRESET_ADMIN);
  const isQESIIntegrationUser: boolean = user.roles.includes(Roles.QESI_INTEGRATION_USER);

  const isPresetEditableOrDeletable = (r: ReportPresetsApiResponse ): boolean => {
    if (r.currentlyInUse)
      return false;
    
    if (r.isPresetCreatedByIntegrationUser)
      return isQESIIntegrationUser;
    
    const isUserOwner: boolean = r.userId === user.userId;
    
    if (r.isPrivate)
      return isUserClientAdmin || isUserOwner;
    else
      return isUserClientAdmin || isUserReportPresetAdmin;
  }

  const buildList = (r: ReportPresetsApiResponse, index: number) => {
    let disabledActionTooltipString = "";

    if (!isPresetEditableOrDeletable(r)) {
        if (r.currentlyInUse) {
          disabledActionTooltipString = "Presets in use cannot be modified.";
        }
        else {
          disabledActionTooltipString = "You do not have sufficient privileges to manage shared presets.";
        }
    }

    return {
      actions: (
        <>
          <FlyoutLink
            content={{
              body: (
                <ColumnConfigurationPanel
                  allPrivatePresetNames={allPrivatePresetNames}
                  setHasConfigurationChanges={setShowWarningModal}
                  presetId={r.reportCustomizationId}
                  reportType={r.exportType}
                  panelMode={PanelMode.Clone}
                  onSuccessfulSubmit={refreshList}
                />
              ),
              title: "Clone Preset",
            }}
            classNames="qes-button-secondary me-2"
            warnOnClose={showWarningModal}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            testId={`manage-report-presets__clone--${index}`}
            size="40"
          >
            <QesIcon iconName={FontAwesome.Clone} description="Clone preset" />
          </FlyoutLink>
          <FlyoutLink
            content={{
              body: (
                <ColumnConfigurationPanel
                  allPrivatePresetNames={allPrivatePresetNames}
                  setHasConfigurationChanges={setShowWarningModal}
                  presetId={r.reportCustomizationId}
                  reportType={r.exportType}
                  panelMode={PanelMode.Edit}
                  onSuccessfulSubmit={refreshList}
                  columnsEndpoint={reportTypeToColumnsEndpointMap.get(r.exportType)}
                />
              ),
              title: "Edit Preset"
            }}
            classNames="qes-button-secondary me-2"
            disabled={!isPresetEditableOrDeletable(r)}
            warnOnClose={showWarningModal}
            modalContent={{
              body: "You will lose unsaved changes. Continue with closing the sidebar?",
              cancelButtonText: "Cancel",
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            testId={`manage-report-presets__edit--${index}`}
            size="40"
          >
            <TooltipWrapper text={disabledActionTooltipString}>
              <QesIcon iconName={FontAwesome.Edit} description="Edit preset" />
            </TooltipWrapper>
          </FlyoutLink>
          <ModalLink
            content={{
              body: `Are you sure you want to delete the preset ${r.name}?`,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => handleDelete(r.reportCustomizationId, r.name),
              confirmButtonText: "Delete",
              title: "Confirm Delete",
            }}
            testId={`manage-report-presets__delete--${index}`}
            classNames="qes-button-secondary danger"
            disabled={!isPresetEditableOrDeletable(r)}
          >
            <TooltipWrapper text={disabledActionTooltipString}>
              <QesIcon iconName={FontAwesome.TrashOutline} description="Delete preset" />
            </TooltipWrapper>
          </ModalLink>
        </>
      ),
      createdBy: r.createdBy,
      key: r.reportCustomizationId.toString(),
      lastUpdated: formatDateTime(r.lastUpdatedDate),
      lastUsed: !!r.lastUsedDate ? formatDateTime(r.lastUsedDate) : "",
      presetName: (
        <FlyoutLink
          key={r.name}
          classNames="qes-button-secondary"
          testId="report-customization__create-new-preset"
          content={{
            body: (
              <ColumnConfigurationPanel
                allPrivatePresetNames={allPrivatePresetNames}
                setHasConfigurationChanges={() => null}
                presetId={r.reportCustomizationId}
                reportType={r.exportType}
                panelMode={PanelMode.View}
              />
            ),
            title: "Column Configuration",
          }}
          size="40"
        >
          {r.name}
        </FlyoutLink>
      ),
      privacy: r.isPrivate ? 'Private' : 'Public',
      reportType: ReportTypeNamesMap.get(r.exportType) || "Unknown",
    }
  };

  useEffect(() => {
    refreshList();
  }, []);

  const allFilteredData = useMemo(() => (
    sortData(apiResponse.map(buildList)).filter((d) => d.presetName.key.toLowerCase().includes(searchValue.toLowerCase()))
  ), [apiResponse, searchValue, showWarningModal, sortedColumn, sortDirection]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return allFilteredData.slice(startIndex, endIndex);
  }, [allFilteredData, currentPage, recordsPerPage]);

  const paginationProps: WafflesPaginationProps = useMemo(() => ({
    changePage: changePage,
    recordsPerPage: recordsPerPage,
    skip: (currentPage - 1) * recordsPerPage,
    totalRecords: allFilteredData.length,
  }), [currentPage, recordsPerPage, allFilteredData.length]);

  return (
    <Page documentTitle="Manage Presets">
      <PageInner className="container-fluid ms-0">
        <PageHeader title="Manage Presets" disableHR={true} />

        <div className="row">
          <div className="col-12">
            <SearchBox
              inputLabel="Search"
              onSubmit={(value) => {
                setSearchValue(value);
                setCurrentPage(1); 
              }}
              testId="manage-report-presets__search"
              fieldsetClass="d-flex"
              placeholder="Search by preset name..."
            />
          </div>

          <div className="col-12">
            <Waffles<ReportPresetsTableData>
              data={paginatedData}
              customSort={updateSortParameters}
              columns={columnConfig}
              fullWidth={true}
              loading={processing || isInitialLoad}
              passThroughSorting={true}
              enablePagination={true}
              paginationProps={paginationProps}
              totalNumberOfRecords={allFilteredData.length}
              testId="manage-report-presets__table"
            />
          </div>
        </div>
      </PageInner>
    </Page>
  );
}